import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import type {
  Bestaetigung,
  TeilnahmeAntwort,
  TeilnahmeErgebnis,
  UnterschriftWeg,
  UnterweisungAbschnitt,
  UnterweisungFrage,
  UnterweisungPublic,
} from './types';
import { istFehlendesSchema, medienUrl } from './sifaService';

/**
 * Die ANONYME Teilnehmerseite `#/unterweisung/<linkToken>` — nur RPCs, keine
 * Tabelle (docs/sifa-unterweisung-plan.md §3, §5). `anon` hat auf keiner
 * `sifa_*`-Tabelle Rechte; alles laeuft ueber `security definer`-Funktionen,
 * die den Token pruefen (Muster crm_quotes_v5.sql / submit_signature).
 *
 * RPC-Namen und Parameter EXAKT wie in supabase/sifa_unterweisungen_rpc.sql
 * (Nr. 83). Wer hier einen Parameter umbenennt, bricht die Seite still: die
 * Datenbank antwortet dann mit PGRST202 „could not find the function", und
 * genau das faengt `istFehlendesSchema` als „Migration offen" ab.
 *
 * Die Richtig-Flags der Antworten erreichen den Browser NIE — die
 * oeffentliche RPC liefert die Fragen ohne sie, und die Auswertung passiert
 * beim Abschluss serverseitig.
 */

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** Erster gesetzter Wert unter mehreren Schluesseln — die RPC darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row[k] !== undefined && row[k] !== null) return row[k];
  return undefined;
};

/** pick fuer die anderen oeffentlichen Dienste (Sammel-Link, Test-Link, Datenschutz). */
export const rpcFeld = pick;

/**
 * Fehler mit dem SQLSTATE der RPC (`P0002` = Sitzung/Teilnahme ungueltig,
 * `54000` = zu viele offene Anlaeufe). Die Seite entscheidet daran, ob sie
 * einen Neustart anbietet oder um Geduld bittet — der Klartext allein reicht
 * dafuer nicht, er ist fuer Menschen.
 */
export class SifaRpcFehler extends Error {
  code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'SifaRpcFehler';
    this.code = code;
  }
}

/** SQLSTATE eines Fehlers, wenn er einen traegt (Supabase-Fehlerobjekt oder SifaRpcFehler). */
export const fehlerCode = (e: unknown): string | null => {
  const code = (e as { code?: unknown } | null)?.code;
  return typeof code === 'string' && code ? code : null;
};

const werfe = (e: unknown): never => {
  throw new SifaRpcFehler(describeError(e), fehlerCode(e));
};

/**
 * jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, eine
 * Tabellen-Antwort (Array) liefert ihre erste Zeile, alles andere ist null.
 */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (Array.isArray(data)) return data.length > 0 ? alsObjekt(data[0]) : null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

/** alsObjekt fuer die anderen oeffentlichen Dienste. */
export const rpcAntwortObjekt = alsObjekt;

/**
 * Abschnitte: die RPC (v2, Nr. 85) liefert PFADE im oeffentlichen
 * Medien-Bucket; die URLs baut `medienUrl` hier im Client (kein Netz, keine
 * Signatur). Die alte Fassung (Nr. 83) liefert keine Pfade — dann bleiben
 * Bild und Video leer, die Seite zeigt nur den Text.
 */
const mapAbschnitt = (a: Zeile): UnterweisungAbschnitt => {
  const bildPfad = strOrNull(pick(a, 'bildPfad', 'bild_pfad'));
  const videoPfad = strOrNull(pick(a, 'videoPfad', 'video_pfad'));
  return {
    id: str(a.id),
    titel: str(a.titel),
    text: str(a.text),
    bildPfad,
    bildUrl: strOrNull(pick(a, 'bildUrl', 'bild_url')) ?? medienUrl(bildPfad),
    videoPfad,
    videoUrl: strOrNull(pick(a, 'videoUrl', 'video_url')) ?? medienUrl(videoPfad),
  };
};

/** Fragen OHNE `richtig` — auch wenn eine RPC es versehentlich mitliefert, kommt es hier nicht durch. */
const mapFragePublic = (f: Zeile): UnterweisungFrage => ({
  id: str(f.id),
  frage: str(f.frage),
  antworten: arr<Zeile>(f.antworten).map((x) => ({ id: str(x.id), text: str(x.text) })),
  // null = am Ende; die Schrittfolge prueft, ob es den Abschnitt noch gibt.
  abschnittId: strOrNull(pick(f, 'abschnittId', 'abschnitt_id')) || null,
});

export const mapUnterweisungPublic = (row: Zeile): UnterweisungPublic => ({
  unterweisungId: str(pick(row, 'unterweisungId', 'unterweisung_id', 'id')),
  titel: str(row.titel),
  beschreibung: strOrNull(row.beschreibung),
  kundeName: str(pick(row, 'kundeName', 'kunde_name', 'projektName', 'project_name')),
  bueroName: str(pick(row, 'bueroName', 'buero_name', 'companyName', 'company_name')),
  inhalt: arr<Zeile>(row.inhalt).map(mapAbschnitt),
  fragen: arr<Zeile>(row.fragen).map(mapFragePublic),
  bestehenProzent: num(pick(row, 'bestehenProzent', 'bestehen_prozent'), 80),
  gueltigMonate: num(pick(row, 'gueltigMonate', 'gueltig_monate'), 12),
  dauerMin: pick(row, 'dauerMin', 'dauer_min') == null ? null : num(pick(row, 'dauerMin', 'dauer_min'), 0),
  sprache: str(row.sprache) || 'de',
});

/** Ergebnis der oeffentlichen RPC: bei nicht bestanden sagt sie, wie viele Anlaeufe noch bleiben. */
export interface TeilnahmeErgebnisPublic extends TeilnahmeErgebnis {
  /** Verbleibende Versuche nach diesem Fehlversuch; null = die RPC hat es nicht gesagt (bestanden oder alte Fassung). */
  versucheRest: number | null;
}

const mapErgebnis = (row: Zeile): TeilnahmeErgebnisPublic => {
  const rest = pick(row, 'versucheRest', 'versuche_rest');
  return {
    teilnahmeId: str(pick(row, 'teilnahmeId', 'teilnahme_id')),
    bestanden: pick(row, 'bestanden') === true,
    punkte: num(pick(row, 'punkte'), 0),
    punkteMax: num(pick(row, 'punkteMax', 'punkte_max'), 0),
    zertifikatNr: strOrNull(pick(row, 'zertifikatNr', 'zertifikat_nr')),
    gueltigBis: strOrNull(pick(row, 'gueltigBis', 'gueltig_bis')),
    versucheRest: rest == null ? null : num(rest, 0),
  };
};

// ─── Kurs laden ─────────────────────────────────────────────────────────────

/**
 * Der Kurs zum Kundenlink (RPC `sifa_unterweisung_public`). `null`, wenn der
 * Link unbekannt, inaktiv oder abgelaufen ist — ODER die Migration noch
 * fehlt. Die Seite zeigt in beiden Faellen „Dieser Link ist nicht (mehr)
 * gueltig"; ein anonymer Besucher bekommt keine Diagnose.
 */
export const ladeUnterweisungPublic = async (linkToken: string): Promise<UnterweisungPublic | null> => {
  const token = (linkToken || '').trim();
  if (!token) return null;
  try {
    const { data, error } = await supabase.rpc('sifa_unterweisung_public', { p_link_token: token });
    if (error) throw error;
    const row = alsObjekt(data);
    return row ? mapUnterweisungPublic(row) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e);
  }
};

// ─── Durchfuehrung ──────────────────────────────────────────────────────────

/**
 * Beginnt eine Durchfuehrung (RPC `sifa_teilnahme_starten`) und liefert das
 * Sitzungstoken, das die Seite in `sessionStorage` haelt — ein Neuladen
 * verliert nichts. `userAgent` ist Diagnose (welches Geraet), kein Riegel.
 */
export const starteTeilnahme = async (linkToken: string, userAgent?: string): Promise<string> => {
  const token = (linkToken || '').trim();
  if (!token) throw new Error('Dieser Link ist nicht gültig.');
  const ua = (userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '') ?? '').slice(0, 500);
  const { data, error } = await supabase.rpc('sifa_teilnahme_starten', { p_link_token: token, p_user_agent: ua || null });
  if (error) return werfe(error);
  const teilnahmeToken = str(data).trim();
  if (!teilnahmeToken) throw new Error('Die Unterweisung konnte nicht gestartet werden — der Link ist nicht (mehr) aktiv.');
  return teilnahmeToken;
};

export interface TeilnahmeFormularDaten {
  vorname: string;
  nachname: string;
  /** ISO `YYYY-MM-DD`. */
  geburtsdatum: string;
  email: string;
}

/**
 * Die v2-RPC fehlt (Nr. 144 nicht gefahren) — dann, und nur dann, faellt der
 * Abschluss auf v1 zurueck. Ausschliesslich PGRST202 („Funktion nicht im
 * Schema-Cache"): jeder andere Fehler, auch ein 42883 aus dem Rumpf von v2,
 * geht unveraendert an die Seite. Ein Rueckfall dort risse die Bestaetigung
 * still heraus.
 */
const istV2Fehlt = (e: unknown): boolean => fehlerCode(e) === 'PGRST202';

/**
 * Schliesst die Durchfuehrung ab: die Datenbank wertet die Fragen aus,
 * gleicht die Person ab (3 von 4), vergibt Zertifikat-Nr. und Gueltigkeit und
 * schreibt GENAU EINMAL (`abgeschlossen_am is null`). Die Eingaben werden
 * dort eingefroren.
 *
 * Seit Nr. 144 ueber `sifa_teilnahme_abschliessen_v2` mit der Bestaetigung
 * (Wortlaut, Fassung der Datenschutzhinweise) — die Datenbank speichert sie
 * mit `bestaetigt_am`. Fehlt v2 (PGRST202), EIN Rueckfall auf v1 ohne
 * Bestaetigung: der Abschluss geht vor, die Bestaetigung ist dann nicht
 * gespeichert. v1 bleibt dafuer unveraendert bestehen.
 *
 * `unterschrift` = PNG-DataURL (SignaturePad) oder null, wenn die Seite den
 * Abschluss erst nach der QR-Bruecke aufruft und die Unterschrift schon
 * serverseitig an der Signaturanfrage haengt.
 */
export const schliesseTeilnahmeAb = async (
  teilnahmeToken: string,
  formular: TeilnahmeFormularDaten,
  antworten: TeilnahmeAntwort[],
  unterschrift: string | null,
  weg: UnterschriftWeg,
  bestaetigung: Bestaetigung,
): Promise<TeilnahmeErgebnisPublic> => {
  const token = (teilnahmeToken || '').trim();
  if (!token) throw new Error('Die Sitzung ist abgelaufen — bitte die Unterweisung über den Link neu beginnen.');
  if (!bestaetigung || bestaetigung.bestaetigt !== true || !(bestaetigung.text || '').trim()) {
    throw new Error('Bitte zuerst die Teilnahme und die Kenntnisnahme der Datenschutzhinweise bestätigen.');
  }
  const v1Parameter = {
    p_teilnahme_token: token,
    p_vorname: (formular.vorname || '').trim(),
    p_nachname: (formular.nachname || '').trim(),
    // '' ist kein Datum: leer geht als NULL, sonst wirft `date` 22007.
    p_geburtsdatum: (formular.geburtsdatum || '').trim() || null,
    p_email: (formular.email || '').trim(),
    p_antworten: (antworten ?? []).map((a) => ({ frageId: a.frageId, antwortIds: a.antwortIds ?? [] })),
    p_unterschrift: unterschrift || null,
    p_unterschrift_weg: weg,
  };
  let antwort = await supabase.rpc('sifa_teilnahme_abschliessen_v2', {
    ...v1Parameter,
    // Der Text geht woertlich, so wie er angezeigt wurde.
    p_bestaetigung: {
      bestaetigt: true,
      text: bestaetigung.text,
      datenschutzId: bestaetigung.datenschutzId ?? null,
      datenschutzFassung: num(bestaetigung.datenschutzFassung, 0),
    },
  });
  if (antwort.error && istV2Fehlt(antwort.error)) {
    antwort = await supabase.rpc('sifa_teilnahme_abschliessen', v1Parameter);
  }
  if (antwort.error) return werfe(antwort.error);
  const row = alsObjekt(antwort.data);
  if (!row) throw new Error('Der Abschluss wurde nicht bestätigt — bitte erneut versuchen.');
  return mapErgebnis(row);
};

// ─── QR-Bruecke PC → Handy ──────────────────────────────────────────────────

/**
 * Legt eine Signaturanfrage an (RPC `sifa_signatur_anfrage_anlegen`) und
 * liefert das QR-Token, das der PC als `#/sign/<qrToken>?u=1` zeigt.
 * Gueltig 10 Minuten (Datenbank).
 */
export const legeSignaturAnfrageAn = async (teilnahmeToken: string): Promise<string> => {
  const token = (teilnahmeToken || '').trim();
  if (!token) throw new Error('Die Sitzung ist abgelaufen.');
  const { data, error } = await supabase.rpc('sifa_signatur_anfrage_anlegen', { p_teilnahme_token: token });
  if (error) return werfe(error);
  const qr = str(data).trim();
  if (!qr) throw new Error('Die Signaturanfrage konnte nicht angelegt werden.');
  return qr;
};

/**
 * Fragt ab, ob das Handy unterschrieben hat (RPC `sifa_signatur_abfragen`,
 * Polling alle 2,5 s). `null` = noch nicht (oder abgelaufen); sonst die
 * PNG-DataURL.
 */
export const frageSignaturAb = async (teilnahmeToken: string, qrToken: string): Promise<string | null> => {
  const t = (teilnahmeToken || '').trim();
  const q = (qrToken || '').trim();
  if (!t || !q) return null;
  const { data, error } = await supabase.rpc('sifa_signatur_abfragen', { p_teilnahme_token: t, p_qr_token: q });
  if (error) return werfe(error);
  const sig = str(data).trim();
  return sig ? sig : null;
};

/**
 * Die HANDY-Seite: schreibt die Unterschrift an die Signaturanfrage (RPC
 * `sifa_submit_signature`). Eigene RPC statt `submit_signature`, weil die
 * Tabelle eine andere ist (`sifa_signatur_anfragen`, ohne user_id).
 * SignaturePadPage ruft sie ueber ihren Parameter `rpc`. `false` = Token
 * unbekannt oder abgelaufen.
 */
export const sendeSignatur = async (qrToken: string, signature: string): Promise<boolean> => {
  const q = (qrToken || '').trim();
  if (!q) throw new Error('Der Signatur-Link ist nicht gültig.');
  if (!(signature || '').trim()) throw new Error('Bitte zuerst unterschreiben.');
  const { data, error } = await supabase.rpc('sifa_submit_signature', { p_qr_token: q, p_signature: signature });
  if (error) return werfe(error);
  return data === true;
};
